import React from 'react';
import { trimUrl } from './links';

// 스킴 없이 쓴 도메인은 흔한 TLD만 링크로 본다 — "et.al", "Fig.1A" 같은 의학 표기 오탐 방지
const BARE_TLDS = 'com|net|org|kr|io|ai|gov|edu|info|me|co|us|uk|jp|cn|de|tv|app|dev';

/**
 * 텍스트에서 URL을 찾아 클릭 가능한 링크로 변환
 */
export function linkify(text: string): React.ReactNode[] {
  if (!text) return [];

  // URL 패턴: http(s)://, www., 도메인.확장자 형태
  const urlPattern = new RegExp(
    `(https?:\\/\\/[^\\s<>"'\`]+)|(www\\.[^\\s<>"'\`]+)|(\\b[a-zA-Z0-9][a-zA-Z0-9-]*(?:\\.[a-zA-Z0-9-]+)*\\.(?:${BARE_TLDS})\\b(?:\\/[^\\s<>"'\`]*)?)`,
    'gi'
  );
  
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;
  let keyIndex = 0;

  // 정규식으로 URL 찾기
  const regex = new RegExp(urlPattern);
  while ((match = regex.exec(text)) !== null) {
    // 이메일 주소의 도메인 부분은 링크가 아니다(본문으로 남긴다)
    if (match[3] && text.charAt(match.index - 1) === '@') continue;

    // URL 추출 및 정규화 — 끝의 문장부호·짝 없는 괄호·붙여 쓴 조사는 링크에서 빼고 본문으로 남긴다
    let url = trimUrl(match[0]);
    if (!url) continue;
    regex.lastIndex = match.index + url.length;
    let displayUrl = url;

    // URL 이전 텍스트 추가
    if (match.index > lastIndex) {
      parts.push(
        <span key={`text-${keyIndex++}`}>
          {text.substring(lastIndex, match.index)}
        </span>
      );
    }
    
    // http(s)://가 없으면 추가
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }

    // 너무 긴 URL은 축약
    if (displayUrl.length > 60) {
      displayUrl = displayUrl.substring(0, 57) + '...';
    }

    // 클릭 가능한 링크로 추가
    parts.push(
      <a
        key={`link-${keyIndex++}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 underline break-all"
        onClick={(e) => e.stopPropagation()} // 부모 클릭 이벤트 방지
      >
        {displayUrl}
      </a>
    );

    lastIndex = regex.lastIndex;
  }

  // 남은 텍스트 추가
  if (lastIndex < text.length) {
    parts.push(
      <span key={`text-${keyIndex++}`}>
        {text.substring(lastIndex)}
      </span>
    );
  }

  return parts.length > 0 ? parts : [text];
}

/**
 * 여러 줄 텍스트를 링크 변환하면서 줄바꿈도 유지
 */
export function linkifyWithLineBreaks(text: string): React.ReactNode[] {
  if (!text) return [];

  const lines = text.split('\n');
  const result: React.ReactNode[] = [];
  let keyIndex = 0;

  lines.forEach((line, lineIndex) => {
    // 각 라인의 링크 변환
    const linkedParts = linkify(line);
    result.push(
      <span key={`line-${keyIndex++}`}>
        {linkedParts}
      </span>
    );

    // 마지막 줄이 아니면 줄바꿈 추가
    if (lineIndex < lines.length - 1) {
      result.push(<br key={`br-${keyIndex++}`} />);
    }
  });

  return result;
}

